import type { SubscriptionResponse } from '@kimuno/api-sdk';
import { palette, spaces } from '@kimuno/theme';
import { Box, DialogContent, FormControl, Link, RadioGroup, Typography } from '@mui/material';
import { format as formatDate, parseISO } from 'date-fns';
import type React from 'react';
import { useContext, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import {
	submitPauseRequest as submitPauseRequestAction,
	updateExperiment as updateExperimentAction,
} from '../../../actions/user.actions';
import PauseIcon from '../../../assets/subscription/pause.svg';
import {
	dateFormat,
	emailAddresses,
	manageSubscriptionSteps,
	suspensionOptions,
} from '../../../constants';
import services from '../../../services';
import { ampliTrackSubscriptionPauseSubmittedEvent } from '../../../services/track.service';
import { NotificationContext } from '../../../state/NotificationContextProvider';
import type { User } from '../../../types/User';
import useAction from '../../../utils/useAction';
import useScreenSize from '../../../utils/useScreenSize';
import DialogActions from '../../common/DialogActions';
import RadioButton from '../../RadioButton';
import type { DialogStep } from '../ManageSubscriptionDialog';

interface PauseSubscriptionDialogContentProps {
	user: User;
	// Optional so the SM-411 Stripe wrapper (`StripePauseDialog`) can render this
	// content without a legacy Zuora subscription — the Stripe path never touches
	// `subscription`. The Zuora branch below guards on presence before using it.
	subscription?: SubscriptionResponse;
	setStep: (step: DialogStep) => void;
	onClose: () => void;
}

const PauseSubscriptionDialogContent: React.FC<PauseSubscriptionDialogContentProps> = (props) => {
	const { t } = useTranslation();
	const { user, subscription, setStep, onClose } = props;
	const { showNotification } = useContext(NotificationContext);
	const { isXsScreen, isSmScreen } = useScreenSize();
	const [pauseSelection, setPauseSelection] = useState<string>('');
	const updateExperiment = useAction(updateExperimentAction);
	const submitPauseRequest = useAction(submitPauseRequestAction);

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setPauseSelection(event.target.value);
	};

	const getSuspensionStartDate = (subscription: SubscriptionResponse) => {
		const nextChargeDate = subscription.paymentPeriod.nextChargeDate;

		if (subscription.paymentPeriod.type === 'fullAmount' || !nextChargeDate) {
			return new Date();
		}

		return parseISO(nextChargeDate);
	};

	const handlePauseRequest = async () => {
		const pauseDuration = Number(pauseSelection);
		const isStripe = !!user?.isStripeUser;

		if (isStripe) {
			// SM-411 Stripe path: persist a `pending` PauseRequests row via the SM-410
			// API. The success handler in the thunk re-fetches subscription-details so
			// the `pause` block populates and the chip flips to "Pause angefordert"
			// without a page reload. On failure (400/409/422/500) surface a generic
			// error notification — the dialog stays open so the user can retry.
			const durationForStripe = pauseDuration as 1 | 2 | 3;
			try {
				await submitPauseRequest({ durationMonths: durationForStripe });
			} catch {
				showNotification({
					message: t('Your pause request could not be submitted'),
					description: t('Please try again later.'),
					severity: 'error',
				});
				return;
			}
		} else if (subscription) {
			// Legacy Zuora path (SM-411 leaves it untouched): flag the experiment
			// + fire the support-team email. There's no server-side persistence beyond
			// the Mongo user's experiments block. Guarded on `subscription` because
			// SM-411 made the prop optional for the Stripe branch above — a non-Stripe
			// user always has a legacy sub at this call site, so the guard is a type
			// narrower, not a functional gate.
			updateExperiment({
				name: 'GRO-6',
				vwoId: 6,
				variation: '2',
				data: {
					isNotVwo: true,
					suspension: {
						status: 'requested',
						duration: pauseDuration,
						date: Date.now(),
					},
				},
			});

			services.notification.sendSupportNotification({
				data: {
					email: user.email,
					userId: user.id,
					date: formatDate(new Date(), dateFormat),
					type: 'suspension',
					duration: pauseDuration,
					startDate: formatDate(getSuspensionStartDate(subscription), dateFormat),
				},
				template: 'pausing',
			});
		}

		if (pauseDuration) ampliTrackSubscriptionPauseSubmittedEvent(pauseDuration);
		showNotification({
			message: t('Your pause request was successful'),
			description: t('We will send you a confirmation email as soon as the process is complete.'),
			severity: 'success',
		});
		onClose();
	};

	return (
		<>
			<DialogContent sx={{ p: 0 }}>
				<Box
					sx={{
						display: 'flex',
						flexDirection: isXsScreen || isSmScreen ? 'column' : 'row',
						alignItems: 'flex-start',
						flex: '1 0 0',
						alignSelf: 'stretch',
					}}
				>
					{!isXsScreen && (
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								alignSelf: 'center',
								'& svg': {
									width: '400px',
									height: '300px',
								},
							}}
						>
							<PauseIcon />
						</Box>
					)}
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'center',
							gap: spaces.lg,
							padding: spaces.lg,
							flex: '1 0 0',
							alignSelf: 'stretch',
							alignItems: 'flex-start',
						}}
					>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: spaces.xs,
								alignItems: 'flex-start',
								alignSelf: 'stretch',
							}}
						>
							<Typography variant="h6" sx={{ color: palette.text.primary }}>
								{t('Take a short break – completely flexible')}
							</Typography>
							<Typography variant="body2" sx={{ color: palette.text.secondary }}>
								{t(
									'Pause your subscription for 1, 2, or 3 months free of charge. ' +
										"Ideal if you don't need support with lesson preparation right now."
								)}
							</Typography>
						</Box>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'flex-start',
								gap: spaces.sm,
								alignSelf: 'stretch',
							}}
						>
							<Typography variant="subtitle1" sx={{ color: palette.text.primary }}>
								{t('Select the period for your break:')}
							</Typography>
							<FormControl
								component="fieldset"
								data-testid="list-radio-buttons"
								sx={{
									display: 'flex',
									alignItems: 'center',
									alignContent: 'center',
									gap: spaces.md,
									alignSelf: 'stretch',
									flexWrap: 'wrap',
								}}
							>
								<RadioGroup
									row
									name="pause-options"
									value={pauseSelection}
									onChange={handleChange}
									sx={{
										display: 'flex',
										flexDirection: isXsScreen || isSmScreen ? 'column' : 'row',
										gap: spaces.md,
										flexWrap: 'wrap',
										width: '100%',
									}}
								>
									{suspensionOptions.map((option) => (
										<RadioButton
											key={option.value}
											option={option}
											selected={pauseSelection === option.value}
										/>
									))}
								</RadioGroup>
							</FormControl>
							<Typography variant="caption" sx={{ color: palette.text.primary }}>
								<Trans
									i18nKey={`For longer breaks (e.g. parental leave), just write to us at <0>${emailAddresses.support}</0>.`}
									components={[
										<Link
											key="support-email-link"
											href={`mailto:${emailAddresses.support}`}
											sx={{
												color: palette.primary.main,
												textDecoration: 'underline',
											}}
										/>,
									]}
								/>
							</Typography>
						</Box>
					</Box>
				</Box>
			</DialogContent>
			<DialogActions
				onBack={() => setStep(manageSubscriptionSteps.manage)}
				primaryButton={{
					text: t('Pause subscription'),
					color: 'primary',
					variant: 'contained',
					onClick: handlePauseRequest,
					disabled: !pauseSelection,
				}}
				secondaryButton={{
					text: t('Cancel'),
					onClick: onClose,
				}}
			/>
		</>
	);
};

export default PauseSubscriptionDialogContent;
